import { useCallback } from 'react';
import { ExpandableTableRow, DataPointType } from '../NewVerificationTab.data';
import { getBaseDataPointName, generateDataPointName } from '../NewVerificationTab.utils';

interface UseRowManagementProps {
  rows: ExpandableTableRow[];
  setRows: (newRows: ExpandableTableRow[]) => void;
}

interface UseRowManagementReturn {
  handleAddSubRow: (rowIndex: number) => void;
  handleDeleteRow: (rowIndex: number) => void;
}

export const useRowManagement = ({ rows, setRows }: UseRowManagementProps): UseRowManagementReturn => {
  const handleAddSubRow = useCallback(
    (rowIndex: number): void => {
      const parentRow = rows[rowIndex];

      if (!parentRow.showAddIcon || !parentRow.dataPointType) {
        return;
      }

      // Define limits for each data point type
      const limits: Record<DataPointType, number> = {
        name: 1,
        address: 1,
        legalIdentifiers: Infinity,
        country: Infinity,
        accountCountry: Infinity,
        bankName: Infinity,
        bankCode: Infinity,
        branchCode: Infinity,
        iban: Infinity,
        swiftCode: Infinity,
        accountNumber: Infinity,
        forFurtherCredit: Infinity,
        clabe: 1,
      };

      const maxAdditional = limits[parentRow.dataPointType] ?? Infinity;

      let childRowCount = 0;
      let insertIndex = rowIndex + 1;

      for (let i = rowIndex + 1; i < rows.length; i++) {
        const currentRow = rows[i];
        if (currentRow.isChildRow && currentRow.dataPointType === parentRow.dataPointType) {
          childRowCount++;
          insertIndex = i + 1;
        } else {
          break;
        }
      }

      // Check if limit is reached
      if (childRowCount >= maxAdditional) {
        return;
      }

      const parentDataPoint = typeof parentRow.dataPoint === 'string' ? parentRow.dataPoint : String(parentRow.dataPoint || '');
      const baseName = getBaseDataPointName(parentDataPoint);

      // Use special labels for first additional name/address
      let newDataPoint: string;
      let newDataPointType: DataPointType;

      if (parentRow.dataPointType === 'legalIdentifiers') {
        // Parent holds "Legal Identifier 1", so first child is "Legal Identifier 2"
        newDataPoint = generateDataPointName('Legal Identifier', childRowCount + 2);
        newDataPointType = 'legalIdentifiers';
      } else if (parentRow.dataPointType === 'name' && childRowCount === 0) {
        newDataPoint = 'Additional Name';
        newDataPointType = parentRow.dataPointType;
      } else if (parentRow.dataPointType === 'address' && childRowCount === 0) {
        newDataPoint = 'Additional Address';
        newDataPointType = parentRow.dataPointType;
      } else {
        const newRowNumber = childRowCount + 2;
        newDataPoint = generateDataPointName(baseName, newRowNumber);
        newDataPointType = parentRow.dataPointType;
      }

      const newRow: ExpandableTableRow = {
        dataPoint: newDataPoint,
        dataPointType: newDataPointType,
        vendor: '-',
        evidence: undefined,
        automatedMatching: 'Unknown',
        manualProcess: '',
        finalReview: '',
        showDeleteIcon: true,
        isChildRow: true,
      };

      const newRows = [...rows];
      newRows.splice(insertIndex, 0, newRow);
      setRows(newRows);
    },
    [rows, setRows],
  );

  const handleDeleteRow = useCallback(
    (rowIndex: number): void => {
      const newRows = [...rows];
      newRows.splice(rowIndex, 1);
      setRows(newRows);
    },
    [rows, setRows],
  );

  return {
    handleAddSubRow,
    handleDeleteRow,
  };
};
